/**
 * Where to send somebody once they have signed in, read from the address they
 * arrived on.
 *
 * A place on this origin and nothing else. A whole URL would let anybody turn
 * the sign-in screen into their own redirector by handing out a link, and the
 * moment after a real Google sign-in on scenix's own host is the moment a
 * person is most likely to believe whatever they land on.
 *
 * The judgement is made by resolving the value the way the browser will and
 * comparing origins, rather than by naming the shapes that are not allowed.
 * Spelling them out has already failed once: the guard this replaced refused
 * "//elsewhere.example" and let "/\elsewhere.example" through, which a browser
 * reads as the same address because the URL standard treats a backslash in an
 * http or https URL as a forward slash (#511). A list of prefixes is a list of
 * the spellings somebody thought of.
 *
 * `href` is the whole of the current address, so resolution happens against
 * exactly what `window.location.replace` would resolve against, and the value
 * returned is the one this function produced rather than the one it was given.
 */
export function continueTo(href: string): string | null {
  let here: URL;
  try {
    here = new URL(href);
  } catch {
    return null;
  }

  const next = here.searchParams.get("next");
  if (!next) return null;

  let destination: URL;
  try {
    destination = new URL(next, here);
  } catch {
    // Not an address at all, which is a refusal rather than a reason to guess.
    return null;
  }

  // `origin` is the string "null" for a scheme that has no origin, and two of
  // those compare equal. Without the first half, a "javascript:" destination
  // would match an origin-less page and be handed straight to `replace`.
  if (destination.origin === "null" || destination.origin !== here.origin) return null;

  // Relative, so what is handed on cannot name a host even if the reasoning
  // above is one day wrong about one.
  return `${destination.pathname}${destination.search}${destination.hash}`;
}
